import { Link } from 'react-router-dom';
import {
    ShieldCheckIcon,
    DevicePhoneMobileIcon,
    BanknotesIcon,
    ChartBarIcon,
    UserGroupIcon,
    HomeIcon,
    CreditCardIcon,
    ArrowRightIcon,
    LockClosedIcon,
    CurrencyDollarIcon,
    PresentationChartLineIcon,
    BuildingLibraryIcon,
    AcademicCapIcon,
    CalculatorIcon,
    DocumentChartBarIcon,
    StarIcon,
    CheckCircleIcon,
    GlobeAmericasIcon,
    ClockIcon,
    PhoneIcon,
    HandRaisedIcon,
    ShieldExclamationIcon,
    TrophyIcon,
    SparklesIcon,
    RocketLaunchIcon,
    HeartIcon,
    ChatBubbleLeftRightIcon
} from '@heroicons/react/24/solid';
import Footer from '../components/common/Footer';
import Navigation from '../components/common/Navigation';
import BackgroundTexture from '../components/common/BackgroundTexture';
import Particles from '../components/common/Particles';
import ScrollToTop from '../components/common/ScrollToTop';
import { useEffect, useRef } from 'react';

const testimonials = [
  {
    name: "Johanthan Maon",
    role: "Small Business Owner",
    content: "First Equity helped me grow my business with their personalized approach to commercial banking.",
    
  },
  {
    name: "Sandra McCoy",
    role: "First-Time Homebuyer",
    content: "The mortgage team made my dream of homeownership a reality with their expert guidance.",
    
  },
  {
    name: "Emily Rodriguez",
    role: "Student",
    content: "Their student banking solutions and financial education resources helped me start my financial journey.",
    
  }
];

const services = [
  {
    name: 'Personal Banking',
    description: 'Smart solutions for your everyday banking needs',
    features: ['Free Checking', 'High-Yield Savings', 'Online & Mobile Banking'],
    icon: BuildingLibraryIcon,
    color: 'indigo'
  },
  {
    name: 'Business Solutions',
    description: 'Empowering businesses to reach their full potential',
    features: ['Business Checking', 'Merchant Services', 'Commercial Lending'],
    icon: ChartBarIcon,
    color: 'purple'
  },
  {
    name: 'Wealth Management',
    description: 'Expert guidance for your financial future',
    features: ['Investment Planning', 'Retirement Solutions', 'Estate Planning'],
    icon: PresentationChartLineIcon,
    color: 'blue'
  }
];

const innovations = [
  {
    title: 'Digital Banking',
    description: 'Bank securely from anywhere with our award-winning mobile app',
    icon: DevicePhoneMobileIcon,
    stats: '98% user satisfaction'
  },
  {
    title: 'Smart Investing',
    description: 'AI-powered investment strategies tailored to your goals',
    icon: SparklesIcon,
    stats: '$2B+ managed assets'
  },
  {
    title: 'Community Impact',
    description: 'Making a difference in our local communities',
    icon: HeartIcon,
    stats: '$5M+ in community investments'
  }
];

const quickLinks = [
  {
    title: 'Online Banking',
    description: 'Access your accounts 24/7',
    icon: LockClosedIcon,
    link: '/login'
  },
  {
    title: 'Loan Calculator',
    description: 'Plan your next big purchase',
    icon: CalculatorIcon,
    link: '/calculators'
  },
  {
    title: 'Schedule Meeting',
    description: 'Talk to a financial advisor',
    icon: ChatBubbleLeftRightIcon,
    link: '/contact'
  }
];

const HomePage = () => {
  const containerRef = useRef(null);

  useEffect(() => {
    let widgetContainer = null;
    const container = containerRef.current;
    
    if (!container) return;

    try {
      // Create widget container
      widgetContainer = document.createElement('div');
      widgetContainer.className = 'tradingview-widget-container';
      
      // Create widget div
      const widgetDiv = document.createElement('div');
      widgetDiv.className = 'tradingview-widget-container__widget';
      widgetContainer.appendChild(widgetDiv);
      
      // Create script
      const script = document.createElement('script');
      script.type = 'text/javascript';
      script.src = 'https://s3.tradingview.com/external-embedding/embed-widget-market-overview.js';
      script.async = true;
      
      const widgetConfig = {
        "colorTheme": "dark",
        "dateRange": "12M",
        "showChart": true,
        "locale": "en",
        "width": "100%",
        "height": "100%",
        "largeChartUrl": "",
        "isTransparent": true,
        "showSymbolLogo": true,
        "showFloatingTooltip": true,
        "plotLineColorGrowing": "rgba(255, 255, 255, 1)",
        "plotLineColorFalling": "rgba(255, 255, 255, 1)",
        "gridLineColor": "rgba(255, 255, 255, 0.1)",
        "scaleFontColor": "rgba(255, 255, 255, 0.5)",
        "belowLineFillColorGrowing": "rgba(41, 98, 255, 0.12)",
        "belowLineFillColorFalling": "rgba(41, 98, 255, 0.12)",
        "belowLineFillColorGrowingBottom": "rgba(41, 98, 255, 0)",
        "belowLineFillColorFallingBottom": "rgba(41, 98, 255, 0)",
        "symbolActiveColor": "rgba(41, 98, 255, 0.12)",
        "tabs": [
          {
            "title": "Markets",
            "symbols": [
              {
                "s": "FOREXCOM:SPXUSD",
                "d": "S&P 500"
              },
              {
                "s": "FOREXCOM:NSXUSD",
                "d": "US 100"
              },
              {
                "s": "FX:EURUSD",
                "d": "EUR/USD"
              },
              {
                "s": "BITSTAMP:BTCUSD",
                "d": "Bitcoin"
              }
            ],
            "originalTitle": "Markets"
          }
        ]
      };

      script.innerHTML = JSON.stringify(widgetConfig);
      
      // Clear existing content
      container.innerHTML = '';
      
      // Append new elements
      container.appendChild(widgetContainer);
      widgetContainer.appendChild(script);
      
    } catch (error) {
      console.error('Error initializing TradingView widget:', error);
    }

    // Cleanup function
    return () => {
      try {
        if (container) {
          container.innerHTML = '';
        }
      } catch (error) {
        console.error('Error cleaning up TradingView widget:', error);
      }
    };
  }, []);

  return (
    <div className="min-h-screen bg-slate-50 relative">
      <Navigation />

      {/* Hero Section - Modern Design */}
      <div className="relative overflow-hidden bg-gradient-to-br from-slate-900 via-indigo-900 to-blue-900">
        {/* Background Elements */}
        <div className="absolute inset-0">
          <div className="absolute inset-0 bg-grid-pattern opacity-10"></div>
          <div className="absolute inset-0 bg-gradient-to-r from-slate-900/90 to-slate-900/50"></div>
        </div>

        <div className="container mx-auto px-4 py-24 lg:py-32">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            {/* Left Column - Content */}
            <div className="relative z-10 space-y-8">
              <div className="inline-flex items-center px-4 py-2 bg-indigo-500/20 rounded-full text-indigo-200 mb-4">
                <SparklesIcon className="h-5 w-5 mr-2" />
                Together as a Community
              </div>
              <h1 className="text-5xl lg:text-7xl font-bold text-white leading-tight">
                Banking that
                <span className="block mt-2 bg-gradient-to-r from-indigo-400 via-purple-400 to-pink-400 text-transparent bg-clip-text">
                  Empowers You
                </span>
              </h1>
              <p className="text-xl text-slate-200 leading-relaxed max-w-xl">
                Experience a new era of financial freedom with First Equity. Where innovative technology 
                meets personal service to help you achieve your dreams.
              </p>
              <div className="flex flex-col sm:flex-row gap-4">
                <Link
                  to="/signup"
                  className="inline-flex items-center justify-center px-8 py-4 text-lg font-medium text-white bg-indigo-600 rounded-xl hover:bg-indigo-700 transition-all duration-200 hover:-translate-y-1 hover:shadow-lg"
                >
                  Get Started
                  <ArrowRightIcon className="ml-2 h-5 w-5" />
                </Link>
                <Link
                  to="/about"
                  className="inline-flex items-center justify-center px-8 py-4 text-lg font-medium text-white border-2 border-white/20 rounded-xl hover:bg-white/10 transition-all duration-200 hover:-translate-y-1"
                >
                  Learn More
                </Link>
              </div>
            </div>

            {/* Right Column - Image */}
            <div className="relative lg:h-[600px] rounded-2xl overflow-hidden">
              <img
                src="/images/hero-banking.jpg"
                alt="Modern Banking Experience"
                className="w-full h-full object-cover rounded-2xl shadow-2xl"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-slate-900/50 to-transparent"></div>
              
              {/* Floating Stats */}
              <div className="absolute bottom-8 left-8 right-8 grid grid-cols-3 gap-4">
                <div className="bg-white/10 backdrop-blur-lg rounded-xl p-4 text-white">
                  <div className="text-2xl font-bold">98%</div>
                  <div className="text-sm text-white/80">Customer Satisfaction</div>
                </div>
                <div className="bg-white/10 backdrop-blur-lg rounded-xl p-4 text-white">
                  <div className="text-2xl font-bold">24/7</div>
                  <div className="text-sm text-white/80">Digital Banking</div>
                </div>
                <div className="bg-white/10 backdrop-blur-lg rounded-xl p-4 text-white">
                  <div className="text-2xl font-bold">$2B+</div>
                  <div className="text-sm text-white/80">Assets Managed</div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Quick Links Bar */}
      <div className="bg-white shadow-lg relative -mt-8 z-20">
        <div className="container mx-auto px-4">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 -translate-y-8">
            {quickLinks.map((link) => (
              <Link
                key={link.title}
                to={link.link}
                className="bg-white rounded-xl p-6 shadow-xl hover:shadow-2xl 
                  transition-all duration-300 hover:-translate-y-1 group"
              >
                <div className="flex items-start space-x-4">
                  <div className="p-3 bg-gradient-to-br from-indigo-500/10 to-purple-500/10 
                    rounded-lg group-hover:scale-110 transition-transform duration-300">
                    <link.icon className="h-6 w-6 text-indigo-600" />
                  </div>
                  <div>
                    <h3 className="text-lg font-semibold text-gray-900">{link.title}</h3>
                    <p className="text-gray-600">{link.description}</p>
                  </div>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </div>

      {/* Services Section */}
      <div className="py-24 bg-white">
        <div className="container mx-auto px-4">
          <div className="text-center max-w-3xl mx-auto mb-16">
            <h2 className="text-3xl lg:text-4xl font-bold text-gray-900 mb-4">
              Comprehensive Financial Solutions
            </h2>
            <p className="text-xl text-gray-600">
              Discover our range of services designed to support your financial journey at every step
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {services.map((service) => (
              <div
                key={service.name}
                className="relative group"
              >
                <div className="absolute inset-0 bg-gradient-to-r from-indigo-500 to-purple-500 
                  rounded-3xl blur-xl opacity-0 group-hover:opacity-30 transition-opacity duration-300"></div>
                <div className="relative bg-white rounded-2xl p-8 shadow-lg hover:shadow-xl 
                  transition-all duration-300 hover:-translate-y-1">
                  <div className={`p-3 bg-${service.color}-500/10 rounded-xl w-fit mb-6`}>
                    <service.icon className={`h-8 w-8 text-${service.color}-600`} />
                  </div>
                  <h3 className="text-xl font-bold text-gray-900 mb-4">{service.name}</h3>
                  <p className="text-gray-600 mb-6">{service.description}</p>
                  <ul className="space-y-3">
                    {service.features.map((feature) => (
                      <li key={feature} className="flex items-center text-gray-700">
                        <CheckCircleIcon className="h-5 w-5 mr-2 text-green-500" />
                        {feature}
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Innovation Highlights */}
      <div className="py-24 bg-gradient-to-br from-slate-900 via-indigo-900 to-blue-900 relative overflow-hidden">
        <div className="absolute inset-0 bg-grid-pattern opacity-10"></div>
        <div className="container mx-auto px-4 relative z-10">
          <div className="text-center max-w-3xl mx-auto mb-16">
            <h2 className="text-3xl lg:text-4xl font-bold text-white mb-4">
              Innovation at Every Step
            </h2>
            <p className="text-xl text-slate-200">
              Leading the way in modern banking with cutting-edge technology and forward-thinking solutions
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {innovations.map((item) => (
              <div
                key={item.title}
                className="bg-white/10 backdrop-blur-lg rounded-2xl p-8 hover:bg-white/20 
                  transition-all duration-300 hover:-translate-y-1"
              >
                <div className="p-3 bg-white/10 rounded-xl w-fit mb-6">
                  <item.icon className="h-8 w-8 text-white" />
                </div>
                <h3 className="text-xl font-bold text-white mb-2">{item.title}</h3>
                <p className="text-slate-300 mb-6">{item.description}</p>
                <div className="text-lg font-semibold text-indigo-300">{item.stats}</div>
            </div>
            ))}
          </div>
        </div>
      </div>

      {/* Testimonials */}
      <div className="py-24 bg-white">
        <div className="container mx-auto px-4">
          <div className="text-center max-w-3xl mx-auto mb-16">
            <h2 className="text-3xl lg:text-4xl font-bold text-gray-900 mb-4">
              Trusted by Our Community
            </h2>
            <p className="text-xl text-gray-600">
              Hear from members who have transformed their financial lives with First Equity
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {testimonials.map((testimonial) => (
              <div
                key={testimonial.name}
                className="bg-gradient-to-br from-slate-50 to-white rounded-2xl p-8 shadow-lg"
              >
                <div className="flex items-center space-x-4 mb-6">
                
                  <div>
                    <h3 className="text-lg font-bold text-gray-900">{testimonial.name}</h3>
                    <p className="text-gray-600">{testimonial.role}</p>
              </div>
              </div>
                <p className="text-gray-700 italic">{testimonial.content}</p>
                <div className="mt-6 flex">
                  {[...Array(5)].map((_, i) => (
                    <StarIcon key={i} className="h-5 w-5 text-yellow-400" />
                  ))}
              </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Community Impact */}
      <div className="py-24 bg-slate-50">
        <div className="container mx-auto px-4">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            <div>
              <h2 className="text-3xl lg:text-4xl font-bold text-gray-900 mb-6">
                Making a Difference in Our Community
              </h2>
              <p className="text-xl text-gray-600 mb-8">
                At First Equity, we believe in giving back. Our commitment to community development 
                goes beyond banking - we're building a better future together.
              </p>
              <div className="grid grid-cols-2 gap-6 mb-8">
                <div className="bg-white rounded-xl p-6 shadow-md">
                  <div className="text-3xl font-bold text-indigo-600 mb-2">$5M+</div>
                  <div className="text-gray-600">Community Investment</div>
                </div>
                <div className="bg-white rounded-xl p-6 shadow-md">
                  <div className="text-3xl font-bold text-purple-600 mb-2">10K+</div>
                  <div className="text-gray-600">Volunteer Hours</div>
                </div>
              </div>
              <Link 
                to="/community"
                className="inline-flex items-center text-indigo-600 font-medium group"
              >
                Learn More About Our Impact
                <ArrowRightIcon className="ml-2 h-5 w-5 group-hover:translate-x-1 transition-transform" />
              </Link>
            </div>
            <div className="relative">
              <div className="absolute -inset-4 bg-gradient-to-r from-indigo-500 to-purple-500 rounded-3xl blur-2xl opacity-30"></div>
              <img
                src="/images/community.webp"
                alt="Community Impact"
                className="relative rounded-3xl shadow-2xl"
              />
            </div>
          </div>
        </div>
      </div>

      {/* CTA Section */}
      <div className="py-24 bg-gradient-to-br from-indigo-600 to-purple-600 relative overflow-hidden">
        <div className="absolute inset-0 bg-grid-pattern opacity-10"></div>
        <div className="container mx-auto px-4 relative z-10">
          <div className="max-w-3xl mx-auto text-center">
            <h2 className="text-3xl lg:text-4xl font-bold text-white mb-6">
              Ready to Transform Your Banking Experience?
            </h2>
            <p className="text-xl text-white/90 mb-8">
              Join First Equity today and discover banking that puts you first. Open an account 
              in minutes and start your journey to financial success.
            </p>
            <div className="flex flex-col sm:flex-row justify-center gap-4">
              <Link
                to="/signup"
                className="inline-flex items-center justify-center px-8 py-4 
                  bg-white text-indigo-600 font-medium rounded-xl shadow-xl 
                  hover:shadow-2xl transition-all duration-300 hover:-translate-y-1"
              >
                Open an Account
              </Link>
              <Link
                to="/contact"
                className="inline-flex items-center justify-center px-8 py-4 
                  border-2 border-white text-white font-medium rounded-xl 
                  hover:bg-white/10 transition-all duration-300 hover:-translate-y-1"
              >
                Talk to an Advisor
              </Link>
            </div>
          </div>
        </div>
      </div>

      <Footer />
      <ScrollToTop />
    </div>
  );
};

export default HomePage; 